import React from 'react';
import { CourseClassVideo } from '@/types';

interface VideoPlayerProps {
    videoUrl?: string;
    vimeoUrl?: string;
    videos?: CourseClassVideo[];
}

type VideoEntry = { provider: 'bunny' | 'vimeo'; url: string };

const VideoPlayer: React.FC<VideoPlayerProps> = ({ videoUrl, vimeoUrl, videos }) => {
    const getIframeSrc = (value?: string) => {
        if (!value) return null;
        const trimmedValue = value.trim();
        const srcMatch = trimmedValue.match(/src=(?:"([^"]+)"|'([^']+)')/i);
        const rawSrc = srcMatch?.[1] || srcMatch?.[2] || trimmedValue;
        if (!rawSrc.startsWith('http://') && !rawSrc.startsWith('https://')) return null;
        // iOS HLS requirement for Bunny embeds
        return rawSrc.replace(/autoplay=true/gi, 'autoplay=false');
    };

    const getVimeoSrc = (url: string): string | null => {
        if (url.includes('player.vimeo.com')) return url.trim();
        const match = url.match(/vimeo\.com\/(\d+)(?:\/([a-f0-9]+))?/);
        if (!match) return null;
        const videoId = match[1];
        const hash = match[2];
        const hashParam = hash ? `h=${hash}&` : '';
        return `https://player.vimeo.com/video/${videoId}?${hashParam}badge=0&autopause=0&player_id=0&app_id=58479`;
    };

    const allVideos: VideoEntry[] = [];

    const bunnySrc = getIframeSrc(videoUrl);
    if (bunnySrc) {
        allVideos.push({ provider: 'bunny', url: bunnySrc });
    } else if (vimeoUrl) {
        const vimeoSrc = getVimeoSrc(vimeoUrl);
        if (vimeoSrc) allVideos.push({ provider: 'vimeo', url: vimeoSrc });
    }

    if (videos) {
        for (const v of videos) {
            const src = v.provider === 'bunny' ? getIframeSrc(v.url) : getVimeoSrc(v.url);
            if (src) allVideos.push({ provider: v.provider, url: src });
        }
    }

    if (allVideos.length === 0) return null;

    return (
        <div className="bg-black">
            {allVideos.map((entry, index) => (
                <div key={index}>
                    {index > 0 && (
                        <div className="flex items-center gap-4 px-6 py-4 border-t border-white/5">
                            <div className="h-px flex-1 bg-white/10" />
                            <span className="text-[11px] font-semibold tracking-[0.2em] uppercase text-white/25">
                                Parte {index + 1}
                            </span>
                            <div className="h-px flex-1 bg-white/10" />
                        </div>
                    )}
                    <div style={{ position: 'relative', paddingTop: '56.25%' }}>
                        <iframe
                            src={entry.url}
                            loading="lazy"
                            style={{
                                border: 0,
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                height: '100%',
                                width: '100%',
                            }}
                            allow={
                                entry.provider === 'bunny'
                                    ? 'accelerometer;gyroscope;autoplay;encrypted-media;picture-in-picture;'
                                    : 'autoplay; fullscreen; picture-in-picture'
                            }
                            allowFullScreen
                        />
                    </div>
                </div>
            ))}
        </div>
    );
};

export default VideoPlayer;
